import type { PlanLimitError } from "../api/types";

/**
 * Il codice che il backend scrive quando una quota di piano è esaurita.
 *
 * Vive nel campo `error`, non in `type`: `enforcePlanLimit` risponde
 * `403 { error: "plan_limit_exceeded", limit, current, max, plan }` e non ha
 * mai risposto altrimenti. Il frontend cercava `type`, `limit_key`,
 * `limit_value`, `current_plan` e `current_usage` — cinque nomi che nel
 * backend non esistono da nessuna parte. Il modale di upsell non poteva
 * aprirsi, per nessun limite, e nessuno se n'era accorto perché il ramo
 * fallisce in silenzio: un `if` che non è mai vero non lascia tracce.
 *
 * Questo file è l'unico posto che sa che forma ha quel 403. Chi lo intercetta
 * (`api/client.ts`), chi decide se mostrarne il modale (`PlanLimitWatcher`) e
 * chi lo classifica dentro un flusso (creazione azienda) passano tutti di qui,
 * così la prossima volta che il contratto cambia c'è un solo punto da
 * aggiornare — e un test che se ne accorge.
 */
export const PLAN_LIMIT_EXCEEDED = "plan_limit_exceeded";

/** Il limite alzato da `POST /company/v2`, l'unico endpoint che lo produce. */
export const MAX_COMPANIES_LIMIT = "max_companies";

/**
 * I due limiti che si alzano solo *dentro* un'azienda: sono per-workspace, e
 * l'upgrade che li scioglie è quello dell'azienda selezionata. Senza azienda
 * selezionata non c'è niente da offrire, quindi il modale resta chiuso.
 *
 * `max_companies` non è di questi: appartiene all'account, e chi lo incontra
 * sta proprio provando a creare l'azienda che non ha ancora — pretendere una
 * selezione lì significherebbe non mostrare mai il modale a chi ne ha bisogno.
 */
const COMPANY_SCOPED_LIMITS = new Set([
  "max_active_job_posts",
  "max_team_members",
]);

interface ErrorLike {
  response?: { status?: unknown; data?: unknown };
}

/**
 * Se una richiesta è stata rifiutata per quota di piano.
 *
 * Guarda lo stato **e** il codice: `error` è un campo generico di questa API e
 * porta prose in mezzo mondo, quindi il 403 è ciò che rende la lettura sicura.
 */
export const isPlanLimitExceededError = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null) return false;
  const { response } = error as ErrorLike;
  if (response?.status !== 403) return false;
  const data = response.data;
  if (typeof data !== "object" || data === null) return false;
  return (data as { error?: unknown }).error === PLAN_LIMIT_EXCEEDED;
};

/**
 * Se il corpo del 403 è completo abbastanza da poterci scrivere sopra un
 * messaggio.
 *
 * Il modale interpola tutti e quattro i valori: senza uno di essi mostrerebbe
 * un buco al posto di un numero, che è peggio del non aprirsi affatto.
 */
export const isPlanLimitPayload = (
  detail: unknown,
): detail is PlanLimitError => {
  if (typeof detail !== "object" || detail === null) return false;
  const d = detail as Record<string, unknown>;
  return (
    d.error === PLAN_LIMIT_EXCEEDED &&
    typeof d.limit === "string" &&
    typeof d.current === "number" &&
    typeof d.max === "number" &&
    typeof d.plan === "string"
  );
};

/**
 * Se quel 403 merita il modale di upsell.
 *
 * È l'unica decisione: dal 2026-08-04 il modale è la sola superficie che parla
 * di quota esaurita — la creazione azienda non ha più un suo alert in linea,
 * proprio perché due voci sullo stesso rifiuto dicevano la stessa cosa due
 * volte. Sta qui e non dentro il componente per poterla leggere in un test:
 * un ramo che non si apre mai non lascia tracce, ed è già successo una volta.
 */
export const shouldOpenPlanLimitModal = (
  detail: PlanLimitError,
  context: { hasSelectedCompany: boolean },
): boolean => {
  // `-1` è "illimitato": nessuno è oltre quota, non c'è niente da dire.
  if (detail.max === -1) return false;
  if (!COMPANY_SCOPED_LIMITS.has(detail.limit)) return true;
  return context.hasSelectedCompany;
};
